import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { equipmentTypeService } from "../services/equipment-type.service";
import type {
	CreateEquipmentTypeRequest,
	EquipmentTypeListParams,
	ErrorResponse,
	UpdateEquipmentTypeRequest,
} from "../types";

export const equipmentTypeKeys = {
	all: ["equipment-types"] as const,
	lists: () => [...equipmentTypeKeys.all, "list"] as const,
	list: (params?: EquipmentTypeListParams) => [...equipmentTypeKeys.lists(), params] as const,
	details: () => [...equipmentTypeKeys.all, "detail"] as const,
	detail: (id: string) => [...equipmentTypeKeys.details(), id] as const,
};

export function useEquipmentTypes(params?: EquipmentTypeListParams) {
	return useQuery({
		queryKey: equipmentTypeKeys.list(params),
		queryFn: () => equipmentTypeService.list(params),
	});
}

export function useEquipmentType(id: string) {
	return useQuery({
		queryKey: equipmentTypeKeys.detail(id),
		queryFn: () => equipmentTypeService.getById(id),
		enabled: !!id,
	});
}

export function useCreateEquipmentType() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateEquipmentTypeRequest) => equipmentTypeService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: equipmentTypeKeys.lists() });
			message.success("Texnika turi muvaffaqiyatli yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Texnika turi yaratishda xatolik";
			message.error(msg);
		},
	});
}

export function useUpdateEquipmentType() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateEquipmentTypeRequest }) =>
			equipmentTypeService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: equipmentTypeKeys.lists() });
			queryClient.invalidateQueries({
				queryKey: equipmentTypeKeys.detail(variables.id),
			});
			message.success("Texnika turi muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Texnika turi yangilashda xatolik";
			message.error(msg);
		},
	});
}

export function useDeleteEquipmentType() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => equipmentTypeService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: equipmentTypeKeys.lists() });
			message.success("Texnika turi muvaffaqiyatli o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Texnika turi o'chirishda xatolik";
			message.error(msg);
		},
	});
}
